import {observable, computed, action, autorun, reaction, runInAction, toJS} from 'mobx';
import queryString from 'query-string';
import urlParse from 'url-parse';
import * as constants from '../model/const';

export class VPayment{
  @observable paymentType;
  @observable errorMessage;
  @observable cancellationMessage;
  @observable customerPaymentMethods = [];

   constructor(flasher, api, navigation, organization, rentalTransaction, rentalObject, businessUnit, 
    bestOffers, searchForm, authentication, personalDataForm, personalAddressForms, vReview, vPersonalData, vBookingWizard) {
    this.flasher = flasher;
    this.api = api;
    this.navigation = navigation;
    this.organization = organization;
    this.rentalTransaction = rentalTransaction;
    this.rentalObject = rentalObject;
    this.businessUnit = businessUnit;
    this.bestOffers = bestOffers;
    this.searchForm = searchForm;
    this.authentication = authentication;
    this.personalDataForm = personalDataForm;
    this.personalAddressForms = personalAddressForms;
    this.vReview = vReview; // TODO: Make form for reservation/booking. Otherwise could cause circular dependency.
    this.vPersonalData = vPersonalData;
    this.vBookingWizard = vBookingWizard;
  }

  @action.bound setPaymentType(paymentType) {
    this.paymentType = paymentType;
  }

  @action.bound setCustomerPaymentMethods(customerPaymentMethods) {
    this.customerPaymentMethods = customerPaymentMethods.slice(0);
  }

  isCCGW(response) {
    return 'DataTransPayment' in response;
  }

  processCCGWRequest(response) {
    const record = response.DataTransPayment[0];
    const url = `${this.organization.ccgwUppUrl}${record.UPPTransactionId}`;
    window.location.assign(url);
  }

  processLink(linkValue) {
    const url = urlParse(linkValue, true);
    if(url.hash === "#/paymentsuccess") {
      const info = {
        orgId: url.query.organizationId,
        trxId: url.query.rentalTransactionId,
        message: url.query.successMessage,
        targetTrxStatus: url.query.targetRentalTransactionStatus,
        trxId: url.query.rentalTransactionId,
      }
      const search = queryString.stringify(info);
      window.location.assign(`#/bookingFinished?${search}`)
    } else if(url.hash === "#/paymentcanceled") {
      const info = {
        cancellationMessage: url.query.errorMessage,
      }
      const search = queryString.stringify(info);
      window.location.assign(`#/bookingWizard/6?${search}`)
    } else if(url.hash === "#/paymenterror") {
      const info = {
        errorMessage: url.query.errorMessage,
      }
      const search = queryString.stringify(info);
      window.location.assign(`#/bookingWizard/6?${search}`);
    }
  }

  @action.bound async finishBooking(prmOrgId, prmTrxId) { 
    try {
      let orgId = prmOrgId || this.orgId;
      let trxId = prmTrxId || this.trxId;
      this.flasher.setOther('Loading.')
      if(this.authentication.loggedIn) {
        console.log('user is logged in');
        let forms = [this.personalAddressForms.find(o => o.refAddressTypeId === constants.ADDRESS_TYPE.POST)];
        if(!this.vPersonalData.usingPostAsBilling) {
          forms.push(this.personalAddressForms.find(o => o.refAddressTypeId === constants.ADDRESS_TYPE.RECHNUNG));
        }
        await this.rentalTransaction.editAddresses(
          forms.filter(o => o.isDirty).map(o => ({
            street: o.values.street,
            addition: o.values.addition,
            postalCode: o.values.postalCode,
            city: o.values.city,
            country: o.values.country,
            refAddressTypeId: o.refAddressTypeId  
          }))
        )     
      } else {
        console.log('user is guest');
        await this.rentalTransaction.setGuestData({
          orgId, 
          trxId, 
          personalDataValues: this.personalDataForm.values, 
          personalAddressesValues: this.personalAddressForms.map(o => ({
            street: o.values.street,
            addition: o.values.addition,
            postalCode: o.values.postalCode,
            city: o.values.city,
            country: o.values.country,
            refAddressTypeId: o.refAddressTypeId  
          })),
        });
      }
      this.vPersonalData.hasBeenSubmitted = false;
      await this.rentalTransaction.confirmSelection({
        orgId,
        trxId,
      });
      let closingResult;
      if(this.vReview.willBook) {
        closingResult = await this.rentalTransaction.doBook({
          orgId, 
          trxId, 
          bookingNote: this.vReview.bookingNote,
          bookingNote2: this.vReview.bookingNote2,
          paymentMethodId: this.paymentType
        });
      } else {
        closingResult = await this.rentalTransaction.doReserve({
          orgId, 
          trxId, 
          bookingNote: this.vReview.bookingNote,
          bookingNote2: this.vReview.bookingNote2,
          paymentMethodId: this.paymentType
        });
      }
      if(this.isCCGW(closingResult)) {
        console.log('yes credit card');
        this.processCCGWRequest(closingResult);
      } else {
        console.log('no credit card');
        this.navigation.goBWFinished();
      }
    }
    catch(e) {
      this.flasher.handleError(e);
      throw(e);
    }
    finally {
      this.flasher.setOther();
    }
  }

  async startOrUpdate(state) {
    console.log('start or updated payment');
    this.vBookingWizard.setVisitedStep(6)
    this.orgId = state.orgId;
    this.trxId = state.trxId;
    this.errorMessage = state.errorMessage;
    this.cancellationMessage = state.cancellationMessage;
    if(this.errorMessage) {
      this.flasher.reportError('Error', this.errorMessage);
      this.errorMessage = undefined;
    }
    if(this.cancellationMessage) {
      this.flasher.reportError('Error', this.cancellationMessage);
      this.cancellationMessage = undefined;
    }
    try {
      this.flasher.setOther('Loading.')
      await Promise.all([
        this.organization.cFetch(state),
        this.rentalTransaction.fetch(state),
      ])
      await this.rentalObject.cFetch({orgId: state.orgId, objId: this.rentalTransaction.refRentalObjectId});
      await this.businessUnit.cFetch({businessUnitId: this.rentalObject.businessUnitId});
      await this.bestOffers.cFetch({
        startDate: this.rentalTransaction.realRentingStartDate, //window.sessionStorage.getItem('bwStartDate'), //this.startDate,  
        endDate: this.rentalTransaction.realRentingEndDate, //window.sessionStorage.getItem('bwEndDate'), //this.endDate, 
        organizationId: this.orgId,
        objectId: this.rentalTransaction.refRentalObjectId, 
        timeUnit: this.searchForm.timeUnit, 
        oldStartDate: undefined, 
        oldEndDate: undefined,
      }) 
      if(this.authentication.loggedIn) {
        const authUser = await this.api.getAuthenticatedUser(this.orgId);
        if(authUser.CustomerOrganizationBusinessPartner && authUser.CustomerOrganizationBusinessPartner.length>0) {
          const cbp = authUser.CustomerOrganizationBusinessPartner[0];
          if(cbp.CustomerGroupPaymentMethods && cbp.CustomerGroupPaymentMethods.length > 0) {
            this.setCustomerPaymentMethods(cbp.CustomerGroupPaymentMethods);
          }
        }
      }
      if(!this.paymentType) {
        const paymentTypes = this.businessUnit.paymentTypes;
        if(paymentTypes.length > 0) {
          this.setPaymentType(paymentTypes[0].id);
        }
      }
    }
    catch(e) {
      this.flasher.handleError(e);
    }
    finally {
      this.flasher.setOther()      
    }
  }

  stop() { 
  }
} 


// WEBPACK FOOTER //
// ./src/view/VPayment.js